import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ChatAdapter } from './chat/chat.adapter';
import { ZendeskModeService } from './zendesk-mode.service';

/**
 * Fachada de controle do chat de atendimento (Zendesk Messenger).
 *
 * Usa o adapter do modo ativo (`ZendeskModeService`), de forma que FAB, menu
 * lateral e o restante da aplicação não precisem conhecer a API do widget.
 *
 * O launcher nativo do Zendesk fica oculto por CSS; quem abre/fecha o painel é
 * o nosso FAB (help-fab) e o menu lateral, via este serviço.
 */
@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private unreadMessagesSubject = new BehaviorSubject<number>(0);
  /** Quantidade de mensagens não lidas do chat (>0 quando fechado e chega mensagem). */
  public readonly unreadMessages$: Observable<number> = this.unreadMessagesSubject.asObservable();
  private unreadListenerRegistered = false;

  // Teto de tentativas para registrar o listener de não lidas DEPOIS que o modo
  // resolve. Cobre com folga o pior caso do Messenger (polling de 5s + carga do
  // script + waitForZendeskReady ~10s). Contado a partir da resolução do modo,
  // não do carregamento da página — ver listenUnreadMessages.
  private readonly UNREAD_REGISTER_TIMEOUT_MS = 120000;

  constructor(
    private zone: NgZone,
    private modeService: ZendeskModeService
  ) { }

  /**
   * Adapter do modo ativo, ou `null` quando não há widget (`off`).
   * Lido a cada chamada porque o modo só é conhecido depois do bootstrap.
   */
  private get adapter(): ChatAdapter | null {
    return this.modeService.activeAdapter;
  }

  /** Abre o painel do chat. No-op se não há widget no ar. */
  public showChatWindow(): void {
    const adapter = this.adapter;

    if (!adapter) {
      return;
    }

    adapter.show();
    // Zera o badge imediatamente ao abrir o chat.
    this.clearUnreadMessages();
  }

  /**
   * Esconde o painel do chat sem encerrar a sessão/conversa.
   * Usado ao abrir o menu do FAB para o chat não ficar sobreposto ao menu.
   */
  public minimizeChatWindow(): void {
    const adapter = this.adapter;

    if (adapter) {
      adapter.minimize();
    }
  }

  /**
   * Zera manualmente o contador de mensagens não lidas.
   * Chamado ao abrir o chat para remover o badge do FAB.
   */
  public clearUnreadMessages(): void {
    this.zone.run(() => this.unreadMessagesSubject.next(0));
  }

  /**
   * Registra o listener de mensagens não lidas do widget ativo e expõe a
   * contagem via `unreadMessages$`. Idempotente.
   *
   * O retry só começa DEPOIS que o modo resolve (`mode$`), não no carregamento
   * da página: o modo depende do login (polling) e o widget ainda leva alguns
   * segundos para carregar. Um timeout fixo a partir do load — como era antes —
   * podia esgotar antes de o widget existir e o badge nunca registrava para
   * quem demorasse no login. Só há o que ouvir se o modo não for `off`.
   */
  public listenUnreadMessages(): void {
    if (this.unreadListenerRegistered) {
      return;
    }

    this.modeService.mode$
      .pipe(
        filter((mode) => mode !== 'off'),
        take(1)
      )
      .subscribe(() => this.registerUnreadListener());
  }

  /** Tenta registrar o listener, re-tentando enquanto o widget não carregou. */
  private registerUnreadListener(): void {
    const onCount = (count: number) => {
      this.zone.run(() => this.unreadMessagesSubject.next(count));
    };

    const register = (): boolean => {
      const adapter = this.adapter;

      return !!adapter && adapter.tryRegisterUnreadListener(onCount);
    };

    if (register()) {
      this.unreadListenerRegistered = true;
      return;
    }

    this.zone.runOutsideAngular(() => {
      const interval = setInterval(() => {
        if (register()) {
          this.unreadListenerRegistered = true;
          clearInterval(interval);
        }
      }, 1000);
      // Desiste após o teto para não deixar o intervalo rodando indefinidamente.
      setTimeout(() => clearInterval(interval), this.UNREAD_REGISTER_TIMEOUT_MS);
    });
  }
}
